import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../services/firebase';
import { onAuthStateChanged, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { Preferences } from '@capacitor/preferences';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Clear auth state and session
  const clearAuth = useCallback(async () => {
    try {
      await Preferences.remove({ key: 'buyer_session' });
      setUser(null);
      setAuthError(null);
      console.log('Auth state cleared');
    } catch (error) {
      console.error('Error clearing auth state:', error);
    }
  }, []);

  // Handle user state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? 'User signed in' : 'No user');
      setUser(user);
      
      if (!user) {
        await clearAuth();
      }
      
      setLoading(false);
    }, async (error) => {
      console.error('Auth state error:', error);
      await clearAuth();
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => {
      console.log('Cleaning up auth listener');
      unsubscribe();
    };
  }, [clearAuth]);

  // Login function
  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setAuthError(null);
      
      // Clear any existing session first
      await clearAuth();
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Login successful for user:', userCredential.user.uid);
      
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error('Login error:', error);
      setAuthError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [clearAuth]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      await signOut(auth);
      await clearAuth();
      console.log('Logout successful');
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [clearAuth]);

  const value = {
    user,
    loading,
    login,
    logout,
    authError,
    clearAuth,
    isAuthenticated: !!user
  };
  
  console.log('AuthProvider render - isAuthenticated:', !!user);

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
